import {
    organizationGroupErrorMessage,
    useAddOrganizationGroupMember,
} from "@/api/organization-groups";
import {
    OrganizationCombobox,
    type EntityComboboxValidity,
} from "@/components/shared/OrganizationCombobox";
import { Button } from "@/components/ui/button";
import { useId, useState } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { toast } from "sonner";

interface AddMemberComboboxProps {
    groupId: number;
}

export function AddMemberCombobox({ groupId }: AddMemberComboboxProps) {
    const labelId = useId();
    const [organizationId, setOrganizationId] = useState<number | null>(null);
    const [organizationValidity, setOrganizationValidity] =
        useState<EntityComboboxValidity>("empty");
    const [conflict, setConflict] = useState<string | null>(null);
    const [showValidation, setShowValidation] = useState(false);
    const addMember = useAddOrganizationGroupMember();
    const submission = useSingleFlight();

    const handleAdd = async () => {
        if (organizationId === null || organizationValidity !== "valid") {
            setShowValidation(true);
            return;
        }
        setConflict(null);
        await submission.run(async () => {
            try {
                await addMember.mutateAsync({
                    id: groupId,
                    data: { organization_id: organizationId },
                });
                setOrganizationId(null);
            } catch (error) {
                const message = organizationGroupErrorMessage(
                    error,
                    "Couldn't add the organization. Please try again."
                );
                setConflict(message);
                toast.error(message);
            }
        });
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="grid min-w-0 flex-1 gap-2">
                    <span
                        id={labelId}
                        className="text-foreground block text-sm font-medium"
                    >
                        Add an organization
                    </span>
                    <div role="group" aria-labelledby={labelId}>
                        <OrganizationCombobox
                            mode="entity"
                            required
                            name="member_organization_id"
                            value={organizationId}
                            onChange={setOrganizationId}
                            showValidation={showValidation}
                            onValidityChange={setOrganizationValidity}
                            disabled={addMember.isPending}
                        />
                    </div>
                </div>
                <Button
                    type="button"
                    onClick={handleAdd}
                    aria-disabled={addMember.isPending}
                    className="shrink-0"
                >
                    Add member
                </Button>
            </div>
            {conflict && (
                <p role="alert" className="text-destructive text-sm">
                    {conflict}
                </p>
            )}
        </div>
    );
}
